import React from 'react';
import { FormattedMessage } from 'react-intl';

import { WelcomeErrorCode } from '../../../../../../../types';
import { PASSWORD_LEAKED_LINK } from '../../../../../../../components/links';

import messages from './WelcomeFormErrors.messages';

type WelcomeFormErrorProps = {
  error: WelcomeErrorCode | null | undefined;
};

export const WelcomeFormErrors = ({ error }: WelcomeFormErrorProps) => {
  switch (error) {
    case 'unknown':
      return (
        <FormattedMessage
          id="welcome.error.unknown"
          defaultMessage="Something went wrong while attempting to sign up. Please try again."
        />
      );
    case 'captcha':
      return (
        <FormattedMessage
          id="welcome.error.captcha"
          defaultMessage="We are having trouble verifying reCAPTCHA for this request. Please try again. If the problem persists, try another browser/device or reach out to Atlassian Support."
        />
      );
    case 'captchaScoreTooLow':
      return (
        <FormattedMessage
          id="welcome.error.captcha.score_too_low"
          defaultMessage="We'd like to make sure it's really you creating your account. Please confirm you're not a robot."
          description="Message asking user to tick the reCAPTCHA checkbox and resubmit the form"
        />
      );
    case 'request_error':
      return (
        <FormattedMessage
          id="welcome.error.request_error"
          defaultMessage="Unable to connect. Check your internet connection or the network settings for your browser and try again."
        />
      );
    case 'passwordLeaked':
      return (
        <FormattedMessage
          {...messages.passwordLeakedXML}
          tagName="p"
          values={{
            'password-leaked-link': chunks => <a href={PASSWORD_LEAKED_LINK}>{chunks}</a>,
          }}
        />
      );
    case 'passwordCommon':
      return (
        <FormattedMessage
          id="welcome.error.passwordCommon"
          defaultMessage="This password is too widely used. Choose a password that's harder to guess."
        />
      );
    case 'invalidPassword':
      return (
        <FormattedMessage id="welcome.error.invalidPassword" defaultMessage="Invalid password" />
      );
    case 'passwordCaseError':
      return (
        <FormattedMessage
          id="welcome.error.passwordCaseError"
          defaultMessage="Your password must contain at least one uppercase and one lowercase letter."
        />
      );
    case 'passwordDigitOrSpecialError':
      return (
        <FormattedMessage
          id="welcome.error.passwordDigitOrSpecialError"
          defaultMessage="Your password must contain at least one digit or special character."
        />
      );
    case 'passwordLengthError':
      return (
        <FormattedMessage
          id="welcome.error.passwordLengthError"
          defaultMessage="Your password must be between 8 and 72 characters long."
        />
      );
    case 'passwordUsedBefore':
      return (
        <FormattedMessage
          id="welcome.error.passwordUsedBefore"
          defaultMessage="You've used that password before. Please choose a different one."
        />
      );
    case 'usernameMustBeEmail':
      return (
        <FormattedMessage
          id="welcome.error.usernameMustBeEmail"
          defaultMessage="Your account username must be a valid email address."
        />
      );
    case 'passwordStrengthErrorWeak':
      return (
        <FormattedMessage
          id="welcome.error.passwordStrengthErrorWeak"
          defaultMessage="Your password must have a minimum strength of weak."
        />
      );
    case 'passwordStrengthErrorFair':
      return (
        <FormattedMessage
          id="welcome.error.passwordStrengthErrorFair"
          defaultMessage="Your password must have a minimum strength of fair."
        />
      );
    case 'passwordStrengthErrorGood':
      return (
        <FormattedMessage
          id="welcome.error.passwordStrengthErrorGood"
          defaultMessage="Your password must have a minimum strength of good."
        />
      );
    case 'passwordStrengthErrorStrong':
      return (
        <FormattedMessage
          id="welcome.error.passwordStrengthErrorStrong"
          defaultMessage="Your password must have a minimum strength of strong."
        />
      );
    case 'passwordStrengthErrorVeryStrong':
      return (
        <FormattedMessage
          id="welcome.error.passwordStrengthErrorVeryStrong"
          defaultMessage="Your password must have a minimum strength of very strong."
        />
      );
    default:
      return (
        <FormattedMessage
          id="welcome.form.random.error"
          defaultMessage="An error occurred while attempting to sign up: {error}. Please try again or reach out to Atlassian Support."
          values={{ error }}
        />
      );
  }
};
